import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import NeonMic from "@/components/NeonMic";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { IMAGES, BRAND } from "@/lib/site";

const MARQUEE_ITEMS = [
    ...BRAND.cry.map((c) => c.toUpperCase()),
    "REAL VOICES",
    "REAL STORIES",
    "TWIN CITIES",
];

const PILLARS = [
    {
        n: "01",
        title: "Artist spotlights",
        copy: "Platforming the creatives, musicians, and makers shaping the Twin Cities scene.",
        img: IMAGES.bentoArtist,
        wide: true,
    },
    {
        n: "02",
        title: "Behind-the-scenes conversations",
        copy: "Real, relaxed, unforgettable talk — authentic and unfiltered.",
        img: IMAGES.bentoBts,
    },
    {
        n: "03",
        title: "Quick check-ins at shows & events",
        copy: "From the stage to the crowd, live in the moment.",
        img: IMAGES.bentoShows,
    },
    {
        n: "04",
        title: "Community, creativity & connection",
        copy: "Introducing someone new and celebrating meaningful work.",
        img: IMAGES.bentoCommunity,
        wide: true,
    },
];

const ROWS = [
    {
        title: "Interviews",
        desc: "Conversations with artists, entrepreneurs, businesses, nonprofits, and community leaders.",
        tag: "Coming soon",
        path: "/interviews",
        testid: "home-row-interviews",
    },
    {
        title: "Community Stories",
        desc: "Local events, neighborhood highlights, and organizations across the Twin Cities.",
        tag: "Coming soon",
        path: "/community-stories",
        testid: "home-row-stories",
    },
    {
        title: "Events",
        desc: "Appearances, community events, performances, and event coverage.",
        tag: "Now booking coverage",
        path: "/events",
        testid: "home-row-events",
    },
];

const CATEGORIES = [
    "Artists",
    "Musicians",
    "Entrepreneurs",
    "Nonprofits",
    "Small business owners",
    "Community leaders",
    "Everyday neighbors",
];

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const ySec = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section
            ref={ref}
            data-testid="home-hero-section"
            className="grain relative overflow-hidden"
        >
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(640px 420px at 14% 18%, rgba(255,0,127,0.13), transparent 60%), radial-gradient(720px 520px at 86% 72%, rgba(139,92,246,0.13), transparent 60%)",
                }}
            />
            <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-12 lg:pb-28 lg:pt-44">
                <div className="lg:col-span-7">
                    <FadeIn delay={0.05}>
                        <p className="eyebrow">
                            Checking In — Twin Cities Community Media
                        </p>
                    </FadeIn>
                    <h1 className="mt-6 font-display text-[11.5vw] font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
                        <MaskedLine delay={0.18}>Every person</MaskedLine>
                        <MaskedLine delay={0.3}>
                            has a{" "}
                            <span className="animate-flicker font-script font-normal normal-case text-neon-pink neon-pink-text">
                                story
                            </span>
                        </MaskedLine>
                        <MaskedLine delay={0.42}>worth telling.</MaskedLine>
                    </h1>
                    <FadeIn delay={0.65}>
                        <p className="mt-7 max-w-xl text-base leading-relaxed text-fog sm:text-lg">
                            {BRAND.tagline} Spotlighting the artists,
                            musicians, entrepreneurs, nonprofits, small
                            businesses, and neighbors who make the Twin Cities
                            thrive.
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.8}>
                        <div className="mt-9 flex flex-wrap items-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-checkin"
                                className="rounded-full bg-neon-pink px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-all hover:neon-box-pink"
                            >
                                Check in with us
                            </Link>
                            <Link
                                to="/interviews"
                                data-testid="hero-cta-interviews"
                                className="group flex items-center gap-2 rounded-full border border-white/20 px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-silver transition-all hover:border-neon-pink hover:text-neon-pink"
                            >
                                Explore interviews
                                <ArrowRight
                                    size={15}
                                    className="transition-transform group-hover:translate-x-1"
                                />
                            </Link>
                        </div>
                    </FadeIn>
                </div>

                <div className="relative lg:col-span-5">
                    <motion.div
                        style={{ y: yMain }}
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="photo-frame relative"
                    >
                        <img
                            src={IMAGES.heroMain}
                            alt="A host in conversation behind a studio microphone"
                            className="h-64 w-full object-cover sm:h-80 lg:h-[440px]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                        <div
                            data-testid="hero-onair-badge"
                            className="absolute left-4 top-4 flex items-center gap-2 border border-neon-pink/60 bg-ink/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-neon-pink backdrop-blur"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-neon-pink" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-pink" />
                            </span>
                            On air
                        </div>
                    </motion.div>

                    <motion.div
                        style={{ y: ySec }}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
                        className="photo-frame absolute -bottom-12 -left-4 hidden w-52 sm:block lg:-left-16 lg:w-60"
                    >
                        <img
                            src={IMAGES.heroSecondary}
                            alt="Behind the mic, prepping for a conversation"
                            className="h-40 w-full object-cover lg:h-44"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.2, delay: 0.9 }}
                        className="pointer-events-none absolute -top-20 right-0 hidden w-40 lg:block"
                    >
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <NeonMic className="w-full" />
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee items={MARQUEE_ITEMS} />

            <section className="py-24 sm:py-32">
                <div className="mx-auto max-w-5xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">The movement</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-3xl font-bold leading-tight sm:text-5xl">
                            Checking In was built on one simple belief —{" "}
                            <span className="font-script text-4xl font-normal normal-case text-neon-pink neon-pink-text sm:text-6xl">
                                every person
                            </span>{" "}
                            has a story worth telling.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-3xl text-lg leading-relaxed text-fog">
                            Every day throughout the Twin Cities, artists,
                            musicians, entrepreneurs, nonprofit organizations,
                            small business owners, community leaders, and
                            everyday neighbors are making a meaningful impact.
                            Many of those stories never make the headlines —
                            but they deserve to be seen, heard, and celebrated.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <div className="flex items-end justify-between gap-6">
                            <div>
                                <p className="eyebrow">What we're doing</p>
                                <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                                    A platform for the people
                                </h2>
                            </div>
                            <p className="hidden max-w-xs text-sm leading-relaxed text-fog md:block">
                                Building a space where talent, passion, and
                                community come together — one check-in at a
                                time.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-12 grid gap-5 lg:grid-cols-2">
                        {PILLARS.map((p, i) => (
                            <Reveal
                                key={p.n}
                                delay={0.06 * i}
                                className={p.wide ? "lg:col-span-2" : ""}
                            >
                                <article
                                    data-testid={`bento-${p.n}`}
                                    className={`group relative overflow-hidden border border-white/10 bg-coal ${
                                        p.wide ? "h-[300px] sm:h-[380px]" : "h-[340px]"
                                    }`}
                                >
                                    <img
                                        src={p.img}
                                        alt={p.title}
                                        className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-transparent" />
                                    <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-8">
                                        <p className="font-mono text-xs tracking-[0.24em] text-neon-pink">
                                            {p.n}
                                        </p>
                                        <h3 className="mt-2 font-display text-xl font-bold uppercase sm:text-2xl">
                                            {p.title}
                                        </h3>
                                        <p className="mt-2 max-w-md text-sm leading-relaxed text-fog">
                                            {p.copy}
                                        </p>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-y border-white/10 bg-coal/40 py-24 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">Latest from the platform</p>
                        <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                            Where to check in next
                        </h2>
                    </Reveal>
                    <div className="mt-10">
                        {ROWS.map((row, i) => (
                            <Reveal key={row.path} delay={0.06 * i}>
                                <Link
                                    to={row.path}
                                    data-testid={row.testid}
                                    className="group flex items-center justify-between gap-6 border-t border-white/10 py-7 transition-colors last:border-b hover:bg-white/[0.02] sm:py-9"
                                >
                                    <div className="flex items-baseline gap-5 sm:gap-10">
                                        <span className="font-mono text-xs text-neon-pink">
                                            0{i + 1}
                                        </span>
                                        <div>
                                            <h3 className="font-display text-2xl font-bold uppercase transition-colors group-hover:text-neon-pink sm:text-4xl">
                                                {row.title}
                                            </h3>
                                            <p className="mt-2 max-w-lg text-sm leading-relaxed text-fog">
                                                {row.desc}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-4">
                                        <span className="hidden rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fog sm:block">
                                            {row.tag}
                                        </span>
                                        <ArrowUpRight
                                            size={22}
                                            className="text-fog transition-all group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-neon-pink"
                                        />
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative overflow-hidden">
                <img
                    src={IMAGES.skyline}
                    alt="The Minneapolis skyline at night"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/75 to-ink" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
                    <Reveal>
                        <p className="eyebrow">Where we call home</p>
                        <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-5xl">
                            Rooted in the{" "}
                            <span className="font-script font-normal normal-case text-neon-pink neon-pink-text">
                                Twin Cities
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <ul className="mt-10 flex flex-wrap gap-3" data-testid="community-categories">
                            {CATEGORIES.map((c) => (
                                <li
                                    key={c}
                                    className="border border-white/15 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-silver backdrop-blur transition-colors hover:border-neon-pink hover:text-neon-pink"
                                >
                                    {c}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            <section className="relative overflow-hidden py-24 text-center sm:py-32">
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(560px 340px at 50% 50%, rgba(255,0,127,0.12), transparent 65%)",
                    }}
                />
                <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8">
                    <Reveal>
                        <p className="font-script text-5xl text-neon-pink neon-pink-text animate-flicker sm:text-6xl">
                            check in.
                        </p>
                        <h2 className="mt-5 font-display text-3xl font-bold uppercase sm:text-5xl">
                            Got a story worth telling?
                        </h2>
                        <p className="mt-5 text-fog sm:text-lg">
                            Whether you're an artist, business owner, community
                            leader, or someone simply curious about what we're
                            building — we'd love to hear from you.
                        </p>
                        <div className="mt-9 flex flex-wrap justify-center gap-4">
                            <Link
                                to="/contact"
                                data-testid="home-cta-story"
                                className="rounded-full bg-neon-pink px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-all hover:neon-box-pink"
                            >
                                Submit a story idea
                            </Link>
                            <Link
                                to="/work-with-cbj"
                                data-testid="home-cta-partner"
                                className="rounded-full border border-white/20 px-7 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-silver transition-all hover:border-neon-pink hover:text-neon-pink"
                            >
                                Partner with CBJ
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
